import React, { useEffect, useState, ChangeEvent } from 'react';
import Page from 'components/Page';
import Input from 'components/Input';
import Button from 'components/Button';
import { ReactComponent as SettingsIcon } from 'assets/icons/icon-cog.svg';
import S3Image from 'components/S3Image';
import Image from 'components/Image';
import { ReactComponent as ClearIcon } from 'assets/icons/icon-close.svg';
import { useHasura } from 'context/ApiState';
import Auth from '@aws-amplify/auth';
import AppInfoCard from './AppInfoCard';
import ChangePasswordButton from './ChangePasswordButton';
import SetupMFAButton from './SetupMFAButton';
import DisableMFAButton from './DisableMFAButton';
import AppUserImageUploader from './AppUserImageUploader';
import { useAuthWrapperContext } from 'toolbox/src/components/AuthWrapper/AuthWrapper';

export default function Settings() {
  const { state, app_user, updateAppUser } = useHasura();
  const { user: cognitoUser, refreshAuthState } = useAuthWrapperContext();

  const [preferredMFA, setPreferredMFA] = useState('NOMFA');
  const getPreferredMFA = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const pref = await Auth.getPreferredMFA(user, {
        bypassCache: true,
      });
      setPreferredMFA(pref);
    } catch (err) {
      console.warn(err);
    }
  };

  const [formData, setFormData] = useState(null);

  useEffect(() => {
    setFormData(app_user);
    getPreferredMFA();
  }, [app_user]);

  const handleChange = (property, newValue) =>
    setFormData({ ...formData, [property]: newValue });

  const removeAppUserImage = async () => {
    try {
      const updateAppUserResult = await updateAppUser({
        image_upload_id: null,
      });
      console.log(updateAppUserResult);
    } catch (err) {
      console.warn(err);
    }
  };

  const saveChanges = async () => {
    const pendingChanges = {
      name: formData?.name,
      email: formData?.email,
    } as any;

    if (formData?.phone_number)
      pendingChanges.phone_number = formData?.phone_number;
    if (formData?.name) pendingChanges.name = formData?.name;

    await updateAppUser(pendingChanges);

    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, pendingChanges);
    Auth.currentUserInfo();
  };

  if (state?.error) return <div>API Error</div>;

  return (
    <Page
      title="Settings"
      icon={
        <SettingsIcon className="inline w-10 h-10 mr-2 -mb-1 -ml-2 text-gray-500" />
      }
    >
      <div className="card">
        <div className="cardBody">
          <aside className="hidden md:block">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">
              User Profile
            </h2>
            <h2 className="mt-6 mb-4 text-sm font-semibold text-gray-600">
              Profile Image
            </h2>
            {formData?.image?.s3_key && (
              <div className="relative flex items-center mb-6 overflow-hidden bg-white rounded-lg">
                <ClearIcon
                  className="absolute top-0 right-0 w-6 h-6 mt-1 mr-1 text-red-500 bg-red-100 border border-red-400 rounded-full cursor-pointer hover:text-white hover:bg-red-600"
                  onClick={() => removeAppUserImage()}
                />
                {formData?.image?.public_url ? (
                  <Image src={formData?.image?.public_url} className="" />
                ) : (
                  <S3Image s3_key={formData?.image?.s3_key} className="" />
                )}
              </div>
            )}

            <AppUserImageUploader />
          </aside>
          <main>
            <Input
              label="Email Address"
              value={(cognitoUser as any)?.username}
              onChange={null}
              disabled
            />

            <Input
              label="Name"
              value={formData?.name}
              type="name"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('name', e.target.value)
              }
            />
          </main>
        </div>
        <footer>
          {!preferredMFA || preferredMFA === 'NOMFA' ? (
            <SetupMFAButton
              callbackFn={() => {
                refreshAuthState();
                getPreferredMFA();
              }}
            />
          ) : (
            <DisableMFAButton
              callbackFn={() => {
                refreshAuthState();
                getPreferredMFA();
              }}
            />
          )}
          <ChangePasswordButton />
          <Button
            onClick={saveChanges}
            className="btn"
            disabled={
              state?.loading ||
              JSON.stringify(formData) === JSON.stringify(app_user)
            }
          >
            Save Changes
          </Button>
        </footer>
      </div>
      <AppInfoCard />
    </Page>
  );
}
